<div class="p-0 bg-gray-50 min-h-screen">
  <!-- ========================================== -->
  <!-- SECTION 1: SKELETON LOADERS -->
  <!-- ========================================== -->
  <ng-container *ngIf="isInitialLoad">
    <div class="mb-6">
      <app-skeleton-loader type="dashboard"></app-skeleton-loader>
    </div>
  </ng-container>

  <!-- ========================================== -->
  <!-- SECTION 2: PAGE CONTENT -->
  <!-- ========================================== -->
  <ng-container *ngIf="!isInitialLoad">
    <div *ngIf="error" class="bg-red-50 text-red-700 p-4 rounded mb-6">
      {{ error }}
    </div>

    <div *ngIf="analytics">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 fade-in">
        <app-stats-card [icon]="'users'" title="Total Patients" value="{{ analytics.summary.total_patients | number }}"
          color="blue"></app-stats-card>

        <app-stats-card [icon]="'userCheck'" title="Active Patients"
          value="{{ analytics.summary.active_followups | number }}" color="emerald"></app-stats-card>
        <app-stats-card [icon]="'userExclamation'" title="High Risk"
          value="{{ analytics.summary.overdue_followups | number }}" color="rose"></app-stats-card>
        <app-stats-card [icon]="'followUP'" title="Followups Due" value="{{ analytics.summary.completion_rate }}%"
          color="amber"></app-stats-card>
      </div>

      <!-- Bottom Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 fade-in">
        <!-- Upcoming Followups -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <div class="flex justify-between items-center mb-4">
            <h2 class="font-semibold text-base text-gray-900">Upcoming Follow-ups</h2>
            <ng-select [(ngModel)]="selectedFollowupFilter" [items]="followupFilterOptions" bindLabel="name"
              bindValue="id" [clearable]="false" [searchable]="false" (change)="fetchUpcomingFollowups()" class="custom-ng-select w-36"></ng-select>
          </div>

          <div *ngIf="selectedFollowupFilter === 'custom'" class="flex flex-wrap items-center gap-2 mb-4">
            <input class="input" type="date" aria-label="From date" [(ngModel)]="customStartDate"
              [max]="customEndDate || null" (change)="fetchUpcomingFollowups()" />
            <span class="text-xs text-gray-500">to</span>
            <input class="input" type="date" aria-label="To date" [(ngModel)]="customEndDate"
              [min]="customStartDate || null" (change)="fetchUpcomingFollowups()" />
          </div>

          <div *ngIf="isLoadingFollowups" class="overflow-y-auto max-h-[350px] pr-2 custom-scrollbar">
            <app-skeleton-loader type="followup-card" [count]="3"></app-skeleton-loader>
          </div>

          <div *ngIf="!isLoadingFollowups" class="space-y-4 overflow-y-auto max-h-[350px] pr-2 custom-scrollbar">
            <div *ngFor="let followup of upcomingFollowups"
              class="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                  <img *ngIf="followup.avatar_image" [src]="followup.avatar_image" [alt]="followup.name"
                    class="w-full h-full object-cover" />
                  <span *ngIf="!followup.avatar_image" class="text-teal-700 font-semibold">{{
                    followup.name.charAt(0)
                    }}</span>
                </div>
                <div>
                  <div class="font-medium text-gray-900">{{ followup.name }}</div>
                  <div class="text-xs text-gray-500">{{ followup.program_name }}</div>

                  <div class="flex flex-row gap-3">
                    <div class="text-sm font-medium text-teal-600 tabular-nums">
                      {{ followup.next_followup_date | date : 'shortTime' }}
                    </div>
                    <div class="text-[10px] text-gray-400">
                      {{ followup.next_followup_date | date : 'mediumDate' }}
                    </div>
                  </div>
                </div>


              </div>
              <div class="text-right flex flex-col items-end">
                <!-- call icon -->
                <div class="p-1.5 hover:bg-teal-50 rounded-full cursor-pointer transition-colors mb-1" role="button"
                  tabindex="0" aria-label="Call patient" (click)="openCallModal(followup)"
                  (keydown.enter)="openCallModal(followup)"
                  (keydown.space)="$event.preventDefault(); openCallModal(followup)">
                  <app-icon name="phone" class="text-teal-600" [size]="18"></app-icon>
                </div>

              </div>
            </div>

            <app-empty-state *ngIf="upcomingFollowups.length === 0" icon="calendar" [iconSize]="36" [compact]="true"
              [title]="isFollowupRangePending ? 'Select a date range' : 'No upcoming follow-ups'"
              [description]="isFollowupRangePending ? 'Pick both From and To dates to load follow-ups.' : 'You\'re all caught up — nothing scheduled right now.'">
            </app-empty-state>
          </div>

          <button
            type="button"
            (click)="openAllFollowupsDrawer()"
            class="text-teal-600 text-sm font-medium inline-flex items-center gap-1 mt-4 hover:text-teal-700 hover:underline cursor-pointer bg-transparent border-0 p-0 text-left">
            View All Follow-ups
            <app-icon name="arrowRight" [size]="14"></app-icon>
          </button>
        </div>

        <!-- Performance -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <div class="flex justify-between items-center mb-4">
            <h2 class="font-semibold text-base text-gray-900">Your Performance</h2>
            <ng-select [(ngModel)]="selectedPeriod" [items]="performanceFilterOptions" bindLabel="name" bindValue="id"
              [clearable]="false" [searchable]="false" (change)="fetchPerformance(selectedPeriod)"
              class="custom-ng-select w-36"></ng-select>
          </div>

          <div *ngIf="isLoadingPerformance">
            <app-skeleton-loader type="stats" [statsCount]="2"></app-skeleton-loader>
          </div>

          <ng-container *ngIf="!isLoadingPerformance">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div class="bg-slate-50/70 border border-slate-100 rounded-lg p-3">
                <div class="text-xs text-gray-500">Calls Made</div>
                <div class="text-xl font-semibold text-gray-900 tabular-nums">{{ performance?.calls_made || 0 }}</div>
              </div>
              <div class="bg-slate-50/70 border border-slate-100 rounded-lg p-3">
                <div class="text-xs text-gray-500">Avg Call Duration</div>
                <div class="text-xl font-semibold text-gray-900 tabular-nums">
                  {{ performance?.avg_call_duration_display || '0m 0s' }}
                </div>
              </div>
            </div>

            <div class="flex items-center gap-5 text-sm p-3 bg-slate-50/70 border border-slate-100 rounded-lg mb-4"
              *ngIf="performance">
              <div class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-full bg-green-500" aria-hidden="true"></span>
                <span class="text-green-600 font-medium tabular-nums">{{ performance.completed_calls }}</span>
                <span class="text-gray-500">Completed</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-full bg-red-500" aria-hidden="true"></span>
                <span class="text-red-500 font-medium tabular-nums">{{ performance.missed_calls }}</span>
                <span class="text-gray-500">Missed</span>
              </div>
            </div>

            <h3 class="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
            <div class="flex flex-col sm:flex-row gap-3">
              <button
                class="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
                (click)="navigateToAddPatient()">
                <app-icon name="plus" [size]="16"></app-icon>
                Add New Patient
              </button>
              <button
                class="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
                (click)="openAddFollowupModal()">
                <app-icon name="calenderplus" [size]="16"></app-icon>
                Add Follow-up
              </button>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-6 fade-in">
        <!-- Follow-up Trends -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <h2 class="font-semibold text-base text-gray-900 mb-3">{{ analytics.followup_trends.title }}</h2>
          <div [chart]="followUpChart" style="width:100%;height:300px;display:block;" role="img"
            [attr.aria-label]="analytics.followup_trends.title + ' trend chart'"></div>
          <p class="text-center mt-3 text-sm text-gray-500">
            Total: <span class="font-medium text-gray-700 tabular-nums">{{ analytics.followup_trends.total | number
              }}</span>
          </p>
        </div>

        <!-- Patient Status -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <h2 class="font-semibold text-base text-gray-900 mb-3">Patient Status Distribution</h2>
          <div [chart]="patientStatusChart" style="width:100%;height:300px;display:block;" role="img"
            aria-label="Patient status distribution chart"></div>
        </div>
      </div>

      <!-- Lower Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 fade-in">
        <!-- Total Calls -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <h2 class="font-semibold text-base text-gray-900 mb-3">{{ analytics.calls_graph.title }}</h2>
          <div [chart]="totalCallsChart" style="width:100%;height:300px;display:block;" role="img"
            [attr.aria-label]="analytics.calls_graph.title + ' chart'"></div>
          <p class="text-center mt-3 text-sm text-gray-500">
            Total: <span class="font-medium text-gray-700 tabular-nums">{{ analytics.calls_graph.total | number
              }}</span>
          </p>
        </div>

        <!-- Total Sales -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5">
          <h2 class="font-semibold text-base text-gray-900 mb-3">{{ analytics.sales_graph.title }}</h2>
          <div [chart]="totalSalesChart" style="width:100%;height:300px;display:block;" role="img"
            [attr.aria-label]="analytics.sales_graph.title + ' chart'"></div>
          <p class="text-center mt-3 text-sm text-gray-500">
            Total: <span class="font-medium text-gray-700 tabular-nums">₹{{ analytics.sales_graph.total | number
              }}</span>
          </p>
        </div>
      </div>


    </div>
  </ng-container>

  <!-- ========================================== -->
  <!-- SECTION 3: ALL MODALS -->
  <!-- ========================================== -->
  <!-- Call Modal -->
  <app-call-modal [isOpen]="showCallModal" [patient]="selectedPatient"
    [patientName]="selectedPatient?.patient_name || selectedPatient?.name || ''"
    [phoneNumber]="selectedPatient?.patient_contact_number || selectedPatient?.contact_number || selectedPatient?.phoneNumber || ''"
    [patientId]="selectedPatient?.patient_id || selectedPatient?.id || ''" (closed)="closeCallModal()"
    (callEnded)="closeCallModal()"></app-call-modal>

  <!-- ========================================== -->
  <!-- SECTION 3.5: ALL FOLLOW-UPS DRAWER -->
  <!-- ========================================== -->
  <app-drawer
    [isOpen]="showAllFollowupsDrawer"
    drawerId="all-followups-drawer"
    title="Upcoming Follow-ups"
    maxWidth="max-w-2xl"
    [showFooter]="true"
    [showCancel]="false"
    saveText=""
    (close)="closeAllFollowupsDrawer()">

    <!-- Custom Drawer Header -->
    <div drawer-header class="flex items-center gap-2.5">
      <h2 class="drawer-title text-base font-semibold text-gray-900 m-0">Upcoming Follow-ups</h2>
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
        {{ filteredDrawerFollowups.length }}
      </span>
    </div>

    <!-- Drawer Body Content -->
    <div class="flex flex-col gap-4 pb-2">
      <!-- Search and Filter Bar -->
      <div class="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center justify-between">
        <!-- Search Input -->
        <div class="relative flex-1">
          <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <app-icon name="search" [size]="15"></app-icon>
          </span>
          <input
            type="text"
            [(ngModel)]="drawerSearchQuery"
            placeholder="Search by patient, PID, phone, program..."
            class="input input-sm input-block pl-9 pr-8 text-sm rounded-lg border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          />
          <button
            *ngIf="drawerSearchQuery"
            type="button"
            (click)="drawerSearchQuery = ''"
            class="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600 text-xs"
            aria-label="Clear search">
            ✕
          </button>
        </div>

        <!-- Filter Dropdown -->
        <ng-select
          [(ngModel)]="drawerFilter"
          [items]="followupFilterOptions"
          bindLabel="name"
          bindValue="id"
          [clearable]="false"
          [searchable]="false"
          appendTo="body"
          (change)="fetchDrawerFollowups(drawerFilter)"
          class="custom-ng-select w-36 shrink-0">
        </ng-select>
      </div>

      <div *ngIf="drawerFilter === 'custom'" class="flex flex-wrap items-center gap-2 mt-3">
        <input class="input" type="date" aria-label="From date" [(ngModel)]="drawerCustomStartDate"
          [max]="drawerCustomEndDate || null" (change)="fetchDrawerFollowups('custom')" />
        <span class="text-xs text-gray-500">to</span>
        <input class="input" type="date" aria-label="To date" [(ngModel)]="drawerCustomEndDate"
          [min]="drawerCustomStartDate || null" (change)="fetchDrawerFollowups('custom')" />
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoadingDrawerFollowups" class="pt-1">
        <app-skeleton-loader type="drawer-followup" [count]="4"></app-skeleton-loader>
      </div>

      <!-- Follow-up Items List -->
      <div *ngIf="!isLoadingDrawerFollowups" class="space-y-3">
        <div
          *ngFor="let followup of filteredDrawerFollowups"
          class="p-3.5 rounded-xl border border-gray-200/80 bg-white hover:border-teal-300 hover:shadow-xs transition-all duration-150 flex flex-col gap-3">
          
          <!-- Top Row: Patient Info & Timing Status -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <!-- Avatar -->
              <div class="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden shrink-0">
                <img *ngIf="followup.avatar_image" [src]="followup.avatar_image" [alt]="followup.name"
                  class="w-full h-full object-cover" />
                <span *ngIf="!followup.avatar_image" class="text-teal-700 font-semibold text-sm">
                  {{ (followup.name || 'P').charAt(0).toUpperCase() }}
                </span>
              </div>

              <!-- Name & Program -->
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-semibold text-sm text-gray-900 truncate">{{ followup.name }}</span>
                  <span *ngIf="followup.patient_pid" class="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                    {{ followup.patient_pid }}
                  </span>
                </div>
                <div class="text-xs text-gray-500 truncate mt-0.5">{{ followup.program_name || 'General Program' }}</div>
              </div>
            </div>

            <!-- Timing Badge -->
            <div class="shrink-0">
              <span
                *ngIf="getFollowupTimingStatus(followup.next_followup_date) === 'overdue'"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
                <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                Overdue
              </span>
              <span
                *ngIf="getFollowupTimingStatus(followup.next_followup_date) === 'today'"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200">
                <span class="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                Today
              </span>
              <span
                *ngIf="getFollowupTimingStatus(followup.next_followup_date) === 'upcoming'"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Upcoming
              </span>
            </div>
          </div>

          <!-- Mid Row: Schedule & Contact Details -->
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600 bg-gray-50/70 rounded-lg px-3 py-2 border border-gray-100">
            <!-- Scheduled Time -->
            <div class="flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400">
                <circle cx="12" cy="12" r="9"></circle>
                <polyline points="12 7 12 12 16 14"></polyline>
              </svg>
              <span class="font-medium text-teal-700">{{ followup.next_followup_date | date : 'shortTime' }}</span>
            </div>

            <!-- Scheduled Date -->
            <div class="flex items-center gap-1.5">
              <app-icon name="calendar" [size]="13" class="text-gray-400"></app-icon>
              <span>{{ followup.next_followup_date | date : 'mediumDate' }}</span>
            </div>

            <!-- Phone Number -->
            <div *ngIf="followup.contact_number" class="flex items-center gap-1.5 font-mono">
              <app-icon name="phone" [size]="13" class="text-gray-400"></app-icon>
              <span>{{ followup.contact_number }}</span>
            </div>
          </div>

          <!-- Bottom Row: Action Buttons -->
          <div class="flex items-center justify-end gap-2 pt-0.5">
            <button
              type="button"
              (click)="rescheduleFollowup(followup)"
              class="btn btn-sm btn-secondary text-xs text-gray-700 border border-gray-200 hover:bg-gray-100 flex items-center gap-1 px-2.5 py-1 h-8 min-h-0">
              <app-icon name="calendar" [size]="13"></app-icon>
              Reschedule
            </button>
            <button
              type="button"
              (click)="openCallModal(followup)"
              class="btn btn-sm btn-primary text-xs flex items-center gap-1.5 px-3 py-1 h-8 min-h-0">
              <app-icon name="phone" [size]="13"></app-icon>
              Call Patient
            </button>
          </div>

        </div>

        <!-- Empty State -->
        <app-empty-state
          *ngIf="filteredDrawerFollowups.length === 0"
          icon="calendar"
          [iconSize]="36"
          [compact]="true"
          [title]="isDrawerRangePending ? 'Select a date range' : drawerSearchQuery ? 'No matching follow-ups' : 'No follow-ups found'"
          [description]="isDrawerRangePending ? 'Pick both From and To dates to load follow-ups.' : drawerSearchQuery ? 'Try adjusting your search query or switching filters.' : 'You have no scheduled follow-ups for this filter.'">
        </app-empty-state>
      </div>
    </div>

    <!-- Drawer Footer -->
    <div drawer-footer class="flex items-center justify-between w-full">
      <span class="text-xs text-gray-500">
        {{ filteredDrawerFollowups.length }} follow-up{{ filteredDrawerFollowups.length === 1 ? '' : 's' }}
      </span>
      <div class="flex items-center gap-2">
        <button type="button" class="btn btn-secondary btn-sm" (click)="closeAllFollowupsDrawer()">
          Close
        </button>
        <button type="button" class="btn btn-primary btn-sm flex items-center gap-1" (click)="openAddFollowupModal()">
          <app-icon name="plus" [size]="14"></app-icon>
          Add Follow-up
        </button>
      </div>
    </div>
  </app-drawer>

  <!-- ========================================== -->
  <!-- SECTION 4: FOLLOW-UP MODAL -->
  <!-- ========================================== -->
  <!-- Add Follow-up Modal -->
  <input class="modal-state" id="add-followup-modal" type="checkbox" [(ngModel)]="showFollowupModal" />
  <div class="modal">
    <label class="modal-overlay"></label>
    <div class="modal-content flex flex-col gap-0">
      <div class="modal-header flex justify-between items-center">
        <p class="text-xl font-medium">Add Follow-up</p>
        <label for="add-followup-modal" class="btn btn-sm btn-circle btn-ghost">✕</label>
      </div>


      <!-- Body -->
      <div class="modal-body">
        <!-- Error Message -->
        <div *ngIf="followupError"
          class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
          <app-icon name="info" class="text-red-500"></app-icon>
          <span class="text-sm font-medium">{{ followupError }}</span>
        </div>

        <!-- Form -->
        <div class="space-y-4">
          <!-- Patient Selection -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Patient *</label>
            <ng-select [(ngModel)]="followupData.patient_id" [items]="patientOptions" bindLabel="name" bindValue="id"
              placeholder="Select a patient" [loading]="isLoadingPatients" [clearable]="true" (open)="loadPatients()"
              [disabled]="isSaving" appendTo="body" class="custom-ng-select">
              <ng-template ng-option-tmp let-item="item">
                <div>
                  <div class="font-medium">{{ item.name }}</div>
                  <div class="text-xs text-gray-500">{{ item.contact_number }}</div>
                </div>
              </ng-template>
            </ng-select>
          </div>

          <!-- Date and Time -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm text-gray-600 mb-1">Date *</label>
              <div class="relative">
                <input class="input input-block pl-10" type="date" [(ngModel)]="followupData.date"
                  [disabled]="isSaving" />
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
              </div>
            </div>
            <div>
              <label class="block text-sm text-gray-600 mb-1">Time *</label>
              <div class="relative">
                <input class="input input-block pl-10" type="time" [(ngModel)]="followupData.time"
                  [disabled]="isSaving" />
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9"></circle>
                    <polyline points="12 7 12 12 16 14"></polyline>
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <!-- Note -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Note</label>
            <textarea class="textarea textarea-block min-h-[100px]" placeholder="Add a note about this follow-up..."
              [(ngModel)]="followupData.note" [disabled]="isSaving"></textarea>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <label class="btn btn-secondary" for="add-followup-modal" [class.pointer-events-none]="isSaving"
          [class.opacity-50]="isSaving">Cancel</label>
        <button class="btn btn-primary" (click)="saveFollowup()" [disabled]="isSaving">
          <span *ngIf="isSaving" class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            Saving...
          </span>
          <span *ngIf="!isSaving">Save Follow-up</span>
        </button>
      </div>
    </div>
  </div>
</div>